import requests from "./httpServices.js";

class PollServices {
  /**
   * Create a new poll
   * @param data - Poll creation data
   * @returns Promise<PollResponse>
   */
  async createPoll(data) {
    try {
      const response = await requests.post(`/polls`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPolls() {
    try {
      const response = await requests.get(`/polls`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getPollById(pollId) {
    try {
      const response = await requests.get(`/polls/${pollId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an existing poll
   * @param pollId - Poll ID
   * @param data - Poll update data
   * @returns Promise<PollResponse>
   */
  async updatePoll(pollId, data) {
    try {
      const response = await requests.put(`/polls/${pollId}`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a poll
   * @param pollId - Poll ID
   * @returns Promise<DeleteResponse>
   */
  async deletePoll(pollId) {
    try {
      const response = await requests.delete(`/polls/${pollId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Submit a vote for a poll
   * @param data - Vote data { pollId, participantId, participantName, optionIndices, timestamp }
   * @returns Promise<VoteResponse>
   */
  async submitVote(data) {
    try {
      const response = await requests.post(`/polls/${data.pollId}/vote`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get poll results
   * @param pollId - Poll ID
   * @returns Promise<PollResultsResponse>
   */
  async getPollResults(pollId) {
    try {
      const response = await requests.get(`/polls/results/${pollId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getUserPolls() {
    try {
      const response = await requests.get(`/polls/user-polls`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a meeting poll vote session (must be called when launching a poll in a meeting)
   * @param data - { meetingId, sessionId, pollId }
   * @returns Promise<MeetingPollVoteResponse>
   */
  async createMeetingPollVote(data) {
    try {
      const response = await requests.post(`/polls/create-meeting-poll-vote`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get poll results for a specific meeting session
   * @param meetingId - Meeting ID
   * @param sessionId - Session ID
   * @param pollId - Poll ID
   * @returns Promise<PollResultsResponse>
   */
  async getPollVoters(pollId) {
    try {
      const response = await requests.get(`/polls/poll-voters/${pollId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getMeetingPollResults(meetingId, sessionId, pollId) {
    try {
      const response = await requests.get(`/polls/poll-results/${meetingId}/${sessionId}/${pollId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch ongoing poll for current meeting
   * @param meetingId - Meeting ID
   * @param sessionId - Session ID
   * @returns Promise<OngoingPollResponse>
   */
  async fetchOngoingPoll(meetingId, sessionId) {
    try {
      const response = await requests.get(`/polls/fetch-ongoing-poll?meetingId=${meetingId}&sessionId=${sessionId}`);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * End a meeting poll vote session
   * @param data - { meetingId, sessionId, pollId }
   * @returns Promise<EndMeetingPollVoteResponse>
   */
  async endMeetingPollVote(data) {
    try {
      const response = await requests.post(`/polls/end-meeting-poll-vote`, data);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   * @param error - Error object
   * @returns Error with formatted message
   */
  handleError(error) {
    console.error('Poll API Error:', error.response?.data || error);

    if (error.response?.data) {
      const { message, errors } = error.response.data;

      // Handle validation errors array
      if (Array.isArray(errors) && errors.length > 0) {
        return new Error(errors.join(", "));
      }

      // Handle validation errors object
      if (errors && typeof errors === "object" && !Array.isArray(errors)) {
        const errorMessages = Object.values(errors).flat().map(e =>
          typeof e === 'object' ? JSON.stringify(e) : String(e)
        );
        return new Error(errorMessages.join(", "));
      }

      if (message) {
        return new Error(message);
      }

      // Fallback: stringify the entire response data
      return new Error(JSON.stringify(error.response.data));
    }

    if (error.message) {
      return new Error(error.message);
    }

    return new Error("An unexpected error occurred");
  }
}

// Export singleton instance
const pollServices = new PollServices();
export default pollServices;
